<template>
  <div class="members-table-wrap">
    <ag-grid-vue
      ref="ag-grid-vue"
      class="ag-theme-alpine"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :enableRtl="directionSystem === 'rtl'"
      :defaultColDef="defaultColDef"
      :modules="modules"
      :tooltipShowDelay="0"
    >
    </ag-grid-vue>
    <div
      class="info-tooltip"
      :class="{ 'info-tooltip-5': getPermissionTypes.length > 3 }"
      v-if="isPermissionsInfoOpen"
    >
      <button class="close-tooltip" @click="isPermissionsInfoOpen = false">
        <img
          src="@/assets/images/close_icon.svg"
          alt="close icon"
          class="icon"
        />
      </button>
      <ul
        class="types"
        :class="{ 'types-items-5': getPermissionTypes.length > 3 }"
      >
        <li
          class="types-item"
          v-for="permission in getPermissionTypes"
          :key="permission"
        >
          <img src="@/assets/images/team-panel/person.svg" alt="person icon"/>
          <div class="explanation">
            <p class="member-title">{{ permission }}</p>
            <p class="text">
              {{ $t(permission.toUpperCase() + '_PERMISSION') }}
            </p>
          </div>
        </li>
      </ul>
    </div>
    <div v-if="openDeletePopup" class="common-popup">
      <div class="common-popup-content">
        <h2 class="common-popup-title">
          {{ $t('REMOVE_MEMBER_MSG_START') }} {{ userName }} {{ $t('REMOVE_MEMBER_MSG_END') }}
        </h2>
        <p class="common-popup-text">
          {{ $t('OPERATION_CANT_BE_RESTORED') }}
        </p>
        <div class="buttons-wrap">
          <button class="small-button cancel-button" @click="toggleDeletePopup()">
            {{ $t('CANCEL') }}
          </button>
          <button class="small-button remove-button" @click="removeTeamMember">
            {{ $t('REMOVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { mapState } from 'vuex'
import selectCellRenderer from '@/components/select-cell-renderer'
import columnHeaderRenderer from '@/components/column-header-renderer'
import { RichSelectModule } from '@ag-grid-enterprise/rich-select'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import { getPermissionTypes, htmlParser } from '@/utilityFunctions'

export default {
  name: 'staff-list-table',

  data () {
    return {
      defaultColDef: {
        flex: 1,
        cellRenderer: (params) => { return params.value },
        getQuickFilterText: params => {
          return htmlParser(params.value)
        }
      },
      isPermissionsInfoOpen: false,
      modules: [RichSelectModule, SetFilterModule],
      openDeletePopup: false,
      deletedRowIndex: null,
      userName: null
    }
  },

  components: {
    AgGridVue,
    /* eslint-disable */
    selectCellRenderer,
    columnHeaderRenderer
    /* eslint-enable */
  },

  mounted () {
    this.$store.dispatch('getTeamMembers', this.$route.params.librarySlug)
  },

  computed: {
    ...mapState({
      directionSystem: state => state.base.directionSystem,
      teamMembers: state => state.teamMembers.teamMembers
    }),
    getPermissionTypes,

    columnDefs () {
      return [
        {
          field: 'NAME',
          headerName: this.$t('NAME'),
          headerComponent: 'columnHeaderRenderer',
          sortable: true,
          // We not use with a default sort-icon,
          // to set a specific another icon.
          unSortIcon: true
        },
        {
          field: 'EMAIL',
          headerName: this.$t('EMAIL'),
          sortable: false
        },
        {
          field: 'PERMISSION',
          headerName: this.$t('ROLE'),
          headerComponent: 'columnHeaderRenderer',
          headerComponentParams: {
            infoIcon: true
          },
          sortable: true,
          unSortIcon: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressMiniFilter: true,
            suppressSelectAll: true,
            values: this.getPermissionTypes
          },
          editable: true,
          singleClickEdit: true,
          // Use with `selectCellRenderer` component
          // for styling the select-options and add a correct icon to selected item.
          cellClass: 'permission-types',
          stopEditingWhenCellsLoseFocus: true,
          cellEditorPopupPosition: 'under',
          cellEditor: 'agRichSelectCellEditor',
          cellEditorPopup: true,
          cellEditorParams: (params) => {
            return {
              values: this.getPermissionTypes.map(perType => {
                return {
                  text: perType,
                  selected: htmlParser(params.value) === perType
                }
              }),
              cellRenderer: 'selectCellRenderer'
            }
          },
          onCellValueChanged: (rowObj) => { this.updatePermission(rowObj) },
          onCellClicked: (params) => {
            // Hide permissions dropdown by additional click
            if (params.node.isSecondClick) {
              params.node.isSecondClick = false
              params.api.stopEditing()
            } else {
              // Reset isSecondClick attribute to all another rows
              params.api.forEachNode(node => {
                node.isSecondClick = false
              })
              params.node.isSecondClick = true
            }
          },
          valueSetter: params => {
            params.data.PERMISSION = params.newValue.text
            return true
          }
        },
        {
          field: 'ORGANIZATION',
          headerName: this.$t('ORGANIZATION'),
          sortable: true,
          unSortIcon: true,
          headerComponent: 'columnHeaderRenderer'
        },
        {
          field: 'REMOVE',
          headerName: '',
          cellClass: 'remove-team-member',
          onCellClicked: (rowObj) => { this.toggleDeletePopup(rowObj) }
        }
      ]
    },
    rowData () {
      return this.teamMembers.map(member => {
        return {
          NAME: member.first_name + ' ' + member.last_name,
          EMAIL: member.email,
          PERMISSION: member.role.charAt(0).toUpperCase() + member.role.slice(1),
          ORGANIZATION: member.organization,
          REMOVE: this.$t('REMOVE')
        }
      })
    }
  },

  methods: {
    togglePermissionsInfo () {
      this.isPermissionsInfoOpen = !this.isPermissionsInfoOpen
    },
    updatePermission (rowData) {
      rowData.node.isSecondClick = false
      if (rowData.oldValue !== rowData.newValue) {
        this.$store.dispatch('updatePermission',
          { teamMemberIndex: rowData.node.rowIndex, permission: rowData.newValue.text || rowData.newValue })
      }
    },
    removeTeamMember () {
      this.$store.dispatch('removeTeamMember', this.deletedRowIndex)
      this.toggleDeletePopup()
    },
    toggleDeletePopup (rowObj = null) {
      if (rowObj) {
        this.deletedRowIndex = rowObj.node.rowIndex
        this.userName = this.teamMembers[rowObj.node.rowIndex].first_name + ' ' + this.teamMembers[rowObj.node.rowIndex].last_name
      }
      this.openDeletePopup = !this.openDeletePopup
    }
  }
}
</script>

<style lang="scss" scoped>

.ag-theme-alpine {
  width: 100%;
  height: 600px;
}

.search-team-member {
  &:after {
    background-image: url('../assets/images/search_grey_icon.svg');
  }

  &:hover, &:focus, &:focus-within {
    &:after {
      background-image: url('../assets/images/search_icon.svg');
    }
  }
}

.sort-button {
  width: 20px;
  height: 13px;

  &.tooltip-btn {
    &:before {
      width: 70px;
      font-size: 0.56rem;
      padding: 4px;
    }

    &:after {
      top: 10%;
    }
  }
}

.cancel-button{
  border-radius: 25px;
  &:hover{transform: scale(1);}
}
.small-button{
  font-size: 15px; padding: 0 20px; height: 22px; line-height: 22px;
}

</style>
